import { useEffect, useState } from 'react';
import { ui } from '../ui';
import {
  describeProviderError,
  signInWithProvider,
  storeSessionToken,
  type SocialResult,
} from '../auth';
import { isLauncher } from '../is-launcher';
import { LauncherIpcError, startSocialSignIn } from '../launcher-ipc';

/**
 * Signing in with Discord, Twitch or Google.
 *
 * Where this audience already is: the community lives on the first, the creator
 * programme is built on the second, and the third is how most people sign in to
 * most things. **Never a requirement** — hard rule 22 says no social account is
 * ever needed to buy a game, so these sit *below* the email form rather than
 * above it, and the page works identically with none of them configured.
 *
 * **TikTok is deliberately absent**, and a configured deployment holds its
 * credentials — they are what a creator proves a TikTok channel with (#91).
 * Login Kit gives an application no email address, so an account created that
 * way could not be recovered or written to; `socialProviders` in
 * `apps/api/src/identity.ts` is where that decision lives, and this list would
 * not draw the button even if it did.
 *
 * ## Only the ones that exist
 *
 * The list comes from `GET /v1/sign-in-options`, which reads the same function
 * the API builds Better Auth's provider map from. A button rendered
 * unconditionally would start an OAuth round trip and come back with an error
 * from Discord — the worst available failure, because it looks like our fault
 * and it happens after somebody has left the site. A fresh clone has no
 * credentials and therefore draws nothing here at all.
 *
 * ## The marks
 *
 * Inlined as paths rather than fetched, because an `<img>` from a provider's
 * CDN on the sign-in page is a third-party request on the one page where a
 * blocked or slow asset is most likely to be read as the page being broken.
 * They take `currentColor`, so they follow the button's own state rather than
 * bringing four more colours onto a surface section 04 gives three.
 *
 * **Google's is the single-colour mark rather than the four-colour G**, and
 * that is the deliberate half. The coloured one is the one their brand
 * guidelines govern — it comes with rules about the white pill it sits in and
 * the clear space around it, none of which survives a button drawn in this
 * system's own vocabulary. A monochrome glyph in `currentColor` is unambiguous
 * about which provider it is and makes no claim to be their asset.
 */

export interface SignInOptions {
  available: string[] | null;
  why: string | null;
  detectedCountry: string | null;
}

export interface Provider {
  id: string;
  label: string;
  icon: React.ReactElement;
}

const DISCORD = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M20.317 4.369A19.79 19.79 0 0 0 15.432 2.9a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.6 12.6 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.009c.12.099.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.891.077.077 0 0 0-.041.107c.36.698.772 1.363 1.225 1.993a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.056c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.028ZM8.02 15.331c-1.182 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418Zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418Z" />
  </svg>
);

const TWITCH = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4.265 0 1.5 4.147v16.588h5.53V24h3.089l2.765-3.265h4.147L23.5 14.53V0H4.265Zm16.47 13.412-3.088 3.088h-5.53l-2.764 2.765v-2.765H4.765V1.941h15.97v11.471ZM16.588 5.53v5.53h-1.941V5.53h1.941Zm-5.53 0v5.53H9.118V5.53h1.94Z" />
  </svg>
);

const GOOGLE = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 2a10 10 0 1 0 9.83 11.9.9.9 0 0 0-.885-1.09H12.9a.9.9 0 0 0-.9.9v1.68c0 .497.403.9.9.9h4.24A5.62 5.62 0 0 1 12 17.62 5.62 5.62 0 1 1 15.86 7.9a.9.9 0 0 0 1.24-.026l1.222-1.222a.9.9 0 0 0-.03-1.303A9.96 9.96 0 0 0 12 2Z" />
  </svg>
);

/**
 * The order they are drawn in, which is not alphabetical.
 *
 * Discord first because it is where the community is and is the one most
 * players arriving here already hold, then Twitch, then Google — which is last
 * because it is the one somebody reaches for when neither of the others is
 * theirs, rather than the one they came looking for.
 *
 * **The list is a filter rather than a promise**, twice over. `offered` below
 * keeps only what `GET /v1/sign-in-options` reports, so an entry here with no
 * credentials behind it draws nothing — and the server's own list is written
 * out rather than derived from what is configured, so a provider whose
 * credentials exist for another purpose does not become a button. TikTok is
 * exactly that case.
 *
 * Exported because sign-up draws these same three as a step of its own rather
 * than as a footer under a form — see `./sign-up`, which asks *how* somebody
 * wants to sign up before it asks anything else. One list, so the two screens
 * cannot come to offer different providers.
 */
export const PROVIDERS: readonly Provider[] = [
  { id: 'discord', label: 'Continue with Discord', icon: DISCORD },
  { id: 'twitch', label: 'Continue with Twitch', icon: TWITCH },
  { id: 'google', label: 'Continue with Google', icon: GOOGLE },
];

/**
 * Where the API is, read once.
 *
 * The same expression `auth.ts` opens with, and a module constant for the same
 * reason: Vite replaces `import.meta.env.VITE_*` at build time, so this is a
 * literal by the time anything runs, and having it in one place stops the
 * sign-in options fetch and the launcher's token exchange from ever pointing at
 * two different APIs.
 */
const API_BASE = import.meta.env['VITE_API_URL'] ?? 'https://api.goodbuild.cc';

/**
 * Social sign-in, under the email form on the sign-in page.
 *
 * The buttons, the wait and the one failure a person can act on. What actually
 * happens when one is pressed is `runProviderSignIn` at the foot of this file —
 * a popup in a browser, the system browser and a loopback listener inside the
 * launcher — and the argument for both is written there rather than here.
 *
 * Where this lands is fixed: the library, because that is the page anybody with
 * a session can open. Sign-up needs a different ending — it has a declaration
 * to record before it goes anywhere — which is why the act and the ending are
 * separate things.
 *
 * **The two paths were two components and are now one**, which is a change
 * worth explaining because the note here used to argue the opposite. They were
 * split so that `isLauncher()` could be branched on before any hook ran —
 * a conditional return in front of hooks being the shape that becomes a
 * hooks-order bug. That reason went away when the branch moved *out* of the
 * component: `runProviderSignIn` below is a plain async function, so what is
 * left on either side is the same three hooks, the same buttons and the same
 * navigation. Keeping them apart was keeping eighty lines duplicated, and the
 * launcher's half is the half that drifts, because nobody exercises it in a
 * browser.
 */
export function SocialSignIn({ mode }: { mode: 'sign-in' | 'sign-up' }): React.ReactElement | null {
  const { available, why } = useSignInOptions();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Null while unknown, so the buttons do not appear a beat after the form and
  // push it up the page under somebody's cursor.
  if (available === null) return null;

  const offered = PROVIDERS.filter((provider) => available.includes(provider.id));

  if (offered.length === 0) return <NoProviders why={why} />;

  async function start(id: string): Promise<void> {
    setBusy(id);
    setError(null);

    const home = `${window.location.origin}/library`;
    const result = await runProviderSignIn(id, home);

    if (!result.ok) {
      // Already a sentence about what happened and what to do next, so it is
      // shown as-is rather than replaced with something vaguer.
      setError(result.message);
      setBusy(null);
      return;
    }

    // A full navigation, like the email form: the whole app is behind the
    // session, and reloading is the simplest way to be sure nothing rendered
    // before it existed is still on screen.
    window.location.href = home;
  }

  return (
    <ProviderButtons
      offered={offered}
      busy={busy}
      mode={mode}
      error={error}
      onStart={(id) => void start(id)}
    />
  );
}

/**
 * What the pages you can reach without an account need before they draw:
 * which providers this deployment offers, why there are none, and where
 * Cloudflare thinks the caller is.
 *
 * Lifted out of `BrowserSocialSignIn` unchanged when the launcher grew a second
 * caller — the retry schedule, the "a 200 is authoritative" rule and the
 * development-only explanation are all exactly as they were. Both surfaces have
 * to ask the same question and both have to be equally wrong-proof about the
 * answer; two copies of sixty lines of retry logic is how one of them quietly
 * stops retrying.
 */
export function useSignInOptions(): SignInOptions {
  const [available, setAvailable] = useState<string[] | null>(null);
  const [detectedCountry, setDetectedCountry] = useState<string | null>(null);
  /**
   * Why there are no buttons, for a developer only.
   *
   * Absent buttons has two causes that render identically — the API said there
   * are no providers, or the API could not be reached — and picking the wrong
   * one costs an afternoon of re-reading a client id that was never wrong. A
   * console warning was not enough, because nothing tells you to open the
   * console when the page looks merely plain.
   *
   * Shown under `import.meta.env.DEV` only. In production an optional
   * enhancement that is switched off must be silent: a visitor has no use for
   * the reason and should not be shown our plumbing.
   */
  const [why, setWhy] = useState<string | null>(null);

  useEffect(() => {
    /*
     * Plain fetch rather than `useApi`: that hook attaches the session token,
     * and by definition nobody on this page has one.
     *
     * **Retried, because the dev API is not up when the page first is.** Every
     * dev block marks its bindings `remote: true`, so `wrangler dev` spends
     * several seconds establishing a remote connection before it serves
     * anything. A single attempt on mount lands inside that window, fails, and
     * leaves the buttons hidden until somebody reloads — and hidden is also the
     * correct render for a deployment with no providers, so it looks like
     * nothing is wrong.
     *
     * Only a *failure to reach* the API is retried. A 200 is authoritative: if
     * the server says there are no providers, asking again will not change its
     * mind.
     */
    const abort = new AbortController();
    const base = API_BASE;

    let cancelled = false;

    async function load(): Promise<void> {
      // Roughly ten seconds in total, which covers a cold `wrangler dev`.
      const delays = [0, 500, 1000, 2000, 4000];

      for (const [attempt, delay] of delays.entries()) {
        if (delay > 0) await new Promise((resolve) => setTimeout(resolve, delay));
        if (cancelled) return;

        try {
          const response = await fetch(`${base}/v1/sign-in-options`, { signal: abort.signal });
          if (!response.ok) throw new Error(`${response.status} from ${base}`);

          const body = (await response.json()) as {
            social?: string[];
            country?: string | null;
          };
          if (cancelled) return;

          const social = body.social ?? [];
          setAvailable(social);
          setDetectedCountry(body.country ?? null);

          if (social.length === 0) {
            setWhy(
              `${base} answered, and reports no social providers configured. ` +
                'Set DISCORD_CLIENT_ID and DISCORD_CLIENT_SECRET in apps/api/.dev.vars ' +
                'and restart the API — it reads that file once, at startup.',
            );
          }
          return;
        } catch (cause) {
          if (cancelled || (cause as Error).name === 'AbortError') return;

          if (attempt === delays.length - 1) {
            /*
             * Said out loud, because otherwise it is indistinguishable from the
             * ordinary case. No buttons is the correct render for a deployment
             * with no provider credentials, so "I set the credentials and
             * nothing appeared" and "the API is not running" produce the
             * identical page — and the first instinct is to go back to the
             * console and re-check an id that was fine.
             */
            const detail = `${base} could not be reached (${(cause as Error).message}).`;
            console.warn(`Sign-in options: ${detail}`);
            setWhy(
              `${detail} The API may not be running, VITE_API_URL may point elsewhere, or the ` +
                'browser may be blocking the response — an origin the API does not allow shows ' +
                'up here as a failed fetch. Check the network tab for /v1/sign-in-options.',
            );
            setAvailable([]);
          }
        }
      }
    }

    void load();

    return () => {
      cancelled = true;
      abort.abort();
    };
  }, []);

  return { available, why, detectedCountry };
}

/** The dev-only explanation for a page with no buttons on it. See `why`. */
function NoProviders({ why }: { why: string | null }): React.ReactElement | null {
  // Silent in production, explained in development.
  if (!import.meta.env.DEV || !why) return null;

  return (
    <p className={`gb-body ${ui.notice ?? ''}`} style={{ fontSize: 'var(--gb-ui-label)' }}>
      <strong>No social sign-in.</strong> {why}
    </p>
  );
}

/**
 * The buttons themselves.
 *
 * A component of its own rather than markup inside `SocialSignIn`, because the
 * "or" rule above them is part of *this* arrangement — an email form with the
 * other ways in underneath — and not part of a provider button. Sign-up draws
 * the same three buttons in a list of its own where there is no form above them
 * to be separated from, and reaches for `PROVIDERS` and `runProviderSignIn`
 * instead of this.
 */
function ProviderButtons({
  offered,
  busy,
  mode,
  error,
  onStart,
}: {
  offered: readonly Provider[];
  busy: string | null;
  mode: 'sign-in' | 'sign-up';
  error: string | null;
  onStart: (id: string) => void;
}): React.ReactElement {
  return (
    <>
      <p className={ui.orRule ?? ''}>or</p>

      {error ? (
        <p className={`gb-body ${ui.notice ?? ''}`} role="alert">
          {error}
        </p>
      ) : null}

      <div className={ui.providers ?? ''}>
        {offered.map((provider) => (
          <button
            key={provider.id}
            type="button"
            className={ui.provider ?? ''}
            disabled={busy !== null}
            onClick={() => onStart(provider.id)}
          >
            {provider.icon}
            {busy === provider.id
              ? 'One moment…'
              : mode === 'sign-up'
                ? provider.label.replace('Continue', 'Sign up')
                : provider.label}
          </button>
        ))}
      </div>
    </>
  );
}

/**
 * One provider sign-in, by whichever route this build has, with the session
 * stored and nothing navigated.
 *
 * **The one place `isLauncher()` is asked**, so that every screen offering
 * these buttons gets the right mechanism without knowing there are two. What
 * comes back is a `SocialResult` either way: `ok`, or a sentence written for a
 * person to read.
 *
 * `callbackURL` is where the *browser* flow would have gone. It is absolute
 * because the API builds the return URL and a relative path would resolve
 * against its origin. The launcher ignores it: its answer comes back over
 * loopback to a listener the Rust side owns, and where that lands is not a URL
 * this side chooses.
 *
 * **It deliberately does not navigate.** The sign-in page goes straight to the
 * library; sign-up has a declaration to record before it goes anywhere. Two
 * endings to the same act, which is the whole reason this is a function
 * returning a result rather than a component that finishes the job.
 */
export async function runProviderSignIn(id: string, callbackURL: string): Promise<SocialResult> {
  if (isLauncher()) return launcherSignIn(id);

  /*
   * A popup, not a redirect, and it is not a preference.
   *
   * A redirect ends in a 302 whose response headers the browser never exposes
   * to script, so the `set-auth-token` the bearer plugin sets on it is
   * unreadable — the session would exist only as a cookie on the API's origin,
   * which `credentials: false` guarantees can never be sent back. Somebody
   * would return from Discord looking exactly as signed out as they left, with
   * nothing erroring anywhere.
   *
   * The popup runs the flow in its own first-party context and posts the token
   * to this window, where `signInWithProvider` stores it. See the note on that
   * function for why it is ours rather than the plugin's.
   */
  return signInWithProvider(id, callbackURL);
}

/**
 * The same sign-in, driven through the system browser.
 *
 * Three hops, and each one exists to keep the session token out of a place it
 * should not be:
 *
 *   1. `start_social_sign_in` binds `127.0.0.1:0`, opens the operating system's
 *      browser at `/v1/launcher/oauth/start`, and waits for exactly one request
 *      on that port. It returns a **code**, never a token.
 *   2. `POST /v1/launcher/oauth/exchange` trades `{ code, state }` for the
 *      session, once. The code is dead afterwards and dead in sixty seconds
 *      regardless.
 *   3. `storeSessionToken` puts it under the one key the rest of the app reads,
 *      which is what makes the session live.
 *
 * **The popup flow cannot run in here, which is why this exists.** Inside a
 * Tauri webview there is no opener relationship to post a token across — a new
 * window, where the webview makes one at all, is not a child of this document —
 * so the promise simply never settled: the button spun until the five-minute
 * timeout and then said the sign-in took too long. That is the worst available
 * failure, because it looks like our fault, it happens after somebody has left
 * the app, and it is indistinguishable from a provider being down.
 *
 * What replaced it is RFC 8252, the arrangement every desktop application ends
 * up at: the flow runs in the system browser, where the person's Discord
 * session and password manager already are, and the answer comes back to a
 * one-shot loopback listener the Rust side owns. `start_social_sign_in` in
 * `apps/launcher/src-tauri/src/main.rs` is that listener;
 * `apps/api/src/routes/launcher-oauth.ts` is the three endpoints it talks to.
 *
 * **Nothing had to change at Discord or Twitch**, and that is the fact that
 * made this worth building rather than a deep link. Both still redirect to
 * Better Auth's own `/auth/callback/:provider`, exactly as the browser flow
 * does; the loopback hop happens one step later, between our API and our
 * launcher. A design that put `http://127.0.0.1:<port>` in front of the
 * *provider* would need a wildcard port registered at each of them, which
 * neither allows.
 *
 * **What crosses loopback is a code, never a session token.** The redirect that
 * reaches the listener is written into browser history and, in most browsers,
 * synced to every other machine that person is signed into. A live token there
 * is a credential on somebody else's disk. So the URL carries a single-use code
 * that dies in sixty seconds, and `POST /v1/launcher/oauth/exchange` is what
 * turns it into a session.
 *
 * ## Every way this can fail, and what is said about each
 *
 * | What happened                        | Where it surfaces                  |
 * | ------------------------------------ | ---------------------------------- |
 * | Provider said no (`access_denied`, …)| `error` on the IPC result          |
 * | Browser closed, or never came back   | rejected IPC call, after 5 minutes |
 * | No browser could be opened           | rejected IPC call, immediately     |
 * | Code refused at the exchange         | a 400 from the API                 |
 *
 * The first row is the important one. Those are Better Auth's own codes and
 * they go through `describeProviderError`, which reads the **same** `MESSAGES`
 * map the browser popup does — `account_not_linked` in particular, which nobody
 * can decode from its name and which means "sign in with your password first,
 * then connect this provider".
 *
 * The second row is honest rather than precise, and it cannot be otherwise: a
 * browser somebody closed and a browser still sitting on a consent screen look
 * identical from a socket that never receives a connection. The sentence covers
 * both instead of guessing.
 */
async function launcherSignIn(id: string): Promise<SocialResult> {
  /**
   * One refusal, said one way.
   *
   * The exchange can fail five different ways — no such state, wrong code,
   * expired, already used, a seal that will not open — and the API answers all
   * five identically on purpose, because distinguishing them would tell
   * somebody holding one of the two secrets something about the other. There is
   * nothing different to do about any of them either.
   */
  const REFUSED = 'That sign-in did not finish. Start it again.';

  try {
    /*
     * This does not resolve for as long as somebody is at the consent screen,
     * and there is no progress to report in between — the Rust side is blocked
     * on a socket. Five minutes, then it gives up. That is the shape of every
     * RFC 8252 sign-in.
     */
    const outcome = await startSocialSignIn(id, API_BASE);

    if (outcome.error) {
      // Better Auth's vocabulary, turned into a sentence by the same map the
      // browser flow uses. Never a second set of words for the same codes.
      return { ok: false, message: describeProviderError(outcome.error) };
    }

    if (!outcome.code) return { ok: false, message: REFUSED };

    /*
     * Plain fetch, no credentials, no session — this *is* how the session
     * begins. `credentials` is left at its default of `omit` for a
     * cross-origin request, which is the same property the rest of this app
     * relies on: the API answers `credentials: false`, so nothing here can ever
     * be sent with ambient authority.
     */
    const response = await fetch(`${API_BASE}/v1/launcher/oauth/exchange`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ code: outcome.code, state: outcome.state }),
    });

    const body = (await response.json().catch(() => ({}))) as {
      token?: unknown;
      error?: unknown;
    };

    if (!response.ok || typeof body.token !== 'string' || body.token === '') {
      return { ok: false, message: typeof body.error === 'string' ? body.error : REFUSED };
    }

    // The whole point of the exercise: the key the rest of the app reads.
    storeSessionToken(body.token);
    return { ok: true };
  } catch (cause) {
    /*
     * `LauncherIpcError` carries the `Err(String)` the Rust command returned,
     * and every one of those is already a sentence written for a person — "The
     * launcher could not open your browser", "The sign-in never came back".
     * Returned as-is for the same reason the browser path returns its own
     * message as-is. Anything else is a bug on this side and gets the generic
     * refusal rather than a stack trace in a paragraph.
     */
    return { ok: false, message: cause instanceof LauncherIpcError ? cause.message : REFUSED };
  }
}
